// ./src/hooks/useCars.ts
//
// CRUD hook for vehicle management.
//
// Aligned with the useExpenses pattern:
// - Race-condition safe via monotonic listCallIdRef
// - Ref-based loadMore/refresh to avoid stale closures
// - No flash of empty content on reset (cars replaced atomically)
// - reset() invalidates in-flight list() calls
// - Mount-time configuration captured in refs

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { DEFAULT_PAGE_SIZE } from "@/config";
import { USER_CAR_ROLES } from "@/config/userCarRoles";

import { ApiError } from "@/utils/apiResultHandler";

import { Pagination, SortItem } from "@/utils/api/commonApiTypes";

import {
  Car,
  CarService,
  CarFilter,
  CarInput,
  UserCar,
} from "@/utils/api/CarService";

import { useApp } from "@/contexts/AppContext";

// ============================================
// Types
// ============================================

export interface UseCarsOptions {
  /**
   * Page size for pagination.
   *
   * This is a **mount-time** configuration value. If you need to change
   * page size after mount, pass it directly to `list()` or `loadMore()`.
   */
  pageSize?: number;

  /**
   * Filters applied on initial autoLoad and userId-change reloads.
   *
   * This is a **mount-time** configuration value. Changing this prop
   * after mount has **no effect** — the hook does not auto-re-fetch.
   * To apply new filters, call `list(newFilters)` explicitly.
   *
   * @default { status: [STATUSES.ACTIVE] }
   */
  initialFilters?: Partial<CarFilter>;

  /**
   * Sort configuration applied on initial autoLoad and userId-change reloads.
   *
   * This is a **mount-time** configuration value. Changing this prop
   * after mount has **no effect** — the hook does not auto-re-fetch.
   * To apply a new sort, pass it directly to `list()`.
   *
   * @default [{ name: "label", order: "ASC" }]
   */
  initialSort?: SortItem[];

  /** Auto-load data on mount (and on userId change). @default true */
  autoLoad?: boolean;
}

export interface UseCarsReturn {
  // Data state
  car: Car | null;
  cars: Car[];
  loading: boolean;
  isLoaded: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<CarFilter>;
  sortBy: SortItem[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  isRemoving: boolean;
  removingIds: Set<string>;
  selectedCar: Car | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<CarFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: CarInput) => Promise<OpResult>;
  update: (id: string, params: CarInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  // UI state management
  setSelectedCar: (car: Car | null) => void;

  // Helper operations
  reset: () => void;

  // Lookup helpers
  carsById: Map<string, Car>;
  getCarById: (id: string | undefined) => Car | undefined;
  getUserCarAssignment: (
    carId: string | undefined,
    userId: string | undefined
  ) => UserCar | undefined;
  getUserRoleForCar: (
    carId: string | undefined,
    userId: string | undefined
  ) => number | null;
  isCarOwner: (carId: string | undefined, userId: string | undefined) => boolean;
  isCarDriver: (carId: string | undefined, userId: string | undefined) => boolean;
  isCarViewer: (carId: string | undefined, userId: string | undefined) => boolean;
  canEditCar: (carId: string | undefined, userId: string | undefined) => boolean;

  // Current user convenience helpers (use userId from context)
  isCurrentUserOwner: (carId: string | undefined) => boolean;
  isCurrentUserDriver: (carId: string | undefined) => boolean;
  canCurrentUserEdit: (carId: string | undefined) => boolean;

  // Error handling
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_SORT: SortItem[] = [{ name: "label", order: "ASC" }];
const DEFAULT_FILTERS: Partial<CarFilter> = { status: [STATUSES.ACTIVE] };

// ============================================
// Hook Implementation
// ============================================

export const useCars = (options: UseCarsOptions = {}): UseCarsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = true,
  } = options;

  // Get user from AppContext
  const { user } = useApp();
  const userId = user?.id ?? null;

  // ── Mount-time configuration captured in refs ──
  // These are read-only after mount. They're used by:
  //   - The userId-change effect (for autoLoad re-fetches)
  //   - loadMore / refresh (pageSize and sort)
  // We use refs (not state) so `list()` and other callbacks don't
  // have stale closures, and so effects don't re-fire on every render.
  const pageSizeRef = useRef(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);

  // Ref to track the last-queried filters for loadMore
  // so we never read stale `filters` state from a closure.
  const lastQueriedFiltersRef = useRef<Partial<CarFilter>>(initialFilters);

  // Ref to track the last-queried sort for loadMore/refresh
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  // Monotonically increasing call ID to detect stale list() responses.
  const listCallIdRef = useRef(0);

  // Track previous userId for change detection
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Ref to track current page for loadMore — avoids stale closures
  // when multiple rapid loadMore calls happen before state settles.
  const currentPageRef = useRef(1);

  // Data state
  const [car, setCar] = useState<Car | null>(null);
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters — tracks what was last queried (for display/debugging)
  const [filters, setFilters] = useState<Partial<CarFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => isLoaded && cars.length === 0,
    [isLoaded, cars.length]
  );

  // Derived: true when any removal is in progress
  const isRemoving = useMemo(
    () => removingIds.size > 0,
    [removingIds]
  );

  // O(1) lookup map for cars by ID
  const carsById = useMemo(
    () => new Map(cars.map((c) => [c.id || "", c])),
    [cars]
  );

  // ============================================
  // Internal Helpers
  // ============================================

  // Helper function to update item in list if it exists, or add it if not
  const updateOrAddInList = useCallback((updatedItem: Car) => {
    setCars((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.id === updatedItem.id
      );
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = updatedItem;
        return newItems;
      }
      return [updatedItem, ...prev];
    });
  }, []);

  // Helper function to remove item from list if it exists
  const removeFromList = useCallback((id: string) => {
    setCars((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Helper function to remove multiple items from list
  const removeManyFromList = useCallback((ids: string[]) => {
    const idsSet = new Set(ids);
    setCars((prev) => prev.filter((item) => !idsSet.has(item.id || "")));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    // Invalidate any in-flight list() calls so their responses are ignored
    listCallIdRef.current++;

    setCar(null);
    setCars([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    currentPageRef.current = 1;
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingIds(new Set());
    setSelectedCar(null);
  }, []);

  // ============================================
  // Core Operations
  // ============================================

  // Get single car
  const get = useCallback(
    async (id: string): Promise<OpResult> => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await CarService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = Array.isArray(result.data)
            ? (result.data as Car[])[0]
            : (result.data as Car);
          setCar(data);
          updateOrAddInList(data);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load car"));
        setCar(null);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load car"));
        setCar(null);
        return result;
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  /**
   * List cars.
   * - shouldReset=true: replaces cars array (page 1 load / filter change)
   * - shouldReset=false: appends to existing cars (load more)
   *
   * Race-condition safe: each call gets a monotonic ID. If a newer call has
   * started by the time the response arrives, the stale response is discarded.
   *
   * Note: This callback has an empty dependency array because it only accesses
   * external values via refs (pageSizeRef, lastQueriedFiltersRef, etc.) and
   * React state setters (setLoading, setCars, etc.) whose identity is
   * guaranteed stable by React across renders.
   */
  const list = useCallback(
    async (
      filter: Partial<CarFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // Don't clear cars here — keep stale data visible while loading.
        // The array is replaced atomically when the response arrives.
        setCurrentPage(1);
        currentPageRef.current = 1;
      }

      setLastError(null);

      // Store the queried filters/sort so loadMore and refresh can use
      // them via refs — avoids stale closures.
      setFilters(filter);
      lastQueriedFiltersRef.current = filter;
      if (sortByParams) {
        setSortBy(sortByParams);
        lastQueriedSortRef.current = sortByParams;
      }

      const currentSortBy = sortByParams || lastQueriedSortRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => sort);

        const result = await CarService.list(
          filter as CarFilter,
          paginationParams,
          sortParams
        );

        // Guard: if a newer list() call was initiated while we were waiting,
        // discard this response to avoid overwriting fresher data.
        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK) {
          const newItems = Array.isArray(result.data)
            ? (result.data as Car[])
            : [];

          if (isAppending) {
            setCars((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(item.id)
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            setCars(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          currentPageRef.current = targetPage;
          setIsLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load cars"));
        setIsLoaded(true);
        if (!isAppending) setCars([]);
        return result;
      } catch (error) {
        // Guard stale responses for error path too
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load cars"));
        setIsLoaded(true);
        if (!isAppending) setCars([]);
        return result;
      } finally {
        // Only update loading flags if this call is still the latest.
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  // Get many cars by IDs
  const getMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        return new OpResult();
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await CarService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedItems = Array.isArray(result.data)
            ? (result.data as Car[])
            : [];
          loadedItems.forEach((item) => {
            updateOrAddInList(item);
          });
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load cars"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load cars"));
        return result;
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // ============================================
  // Mutation Operations
  // ============================================

  // Create car
  const create = useCallback(
    async (params: CarInput): Promise<OpResult> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await CarService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItem = Array.isArray(result.data)
            ? (result.data as Car[])[0]
            : (result.data as Car);
          setCar(newItem);
          setCars((prev) => [newItem, ...prev]);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to create car"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to create car"));
        return result;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update car
  const update = useCallback(
    async (id: string, params: CarInput): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Car ID is required for updating"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await CarService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = Array.isArray(result.data)
            ? (result.data as Car[])[0]
            : (result.data as Car);
          setCar(updatedItem);
          updateOrAddInList(updatedItem);
          setSelectedCar((prev) =>
            prev?.id === id ? updatedItem : prev
          );
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to update car"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to update car"));
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList]
  );

  // Remove car
  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Car ID is required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setRemovingIds((prev) => new Set(prev).add(id));
      setLastError(null);

      try {
        const result = await CarService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setCar(null);
          removeFromList(id);
          setSelectedCar((prev) => (prev?.id === id ? null : prev));
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove car"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to remove car"));
        return result;
      } finally {
        setRemovingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [removeFromList]
  );

  // Remove many cars
  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "At least one car ID is required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setRemovingIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.add(id));
        return next;
      });
      setLastError(null);

      try {
        const result = await CarService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setCar(null);
          removeManyFromList(ids);
          const idsSet = new Set(ids);
          setSelectedCar((prev) =>
            prev?.id && idsSet.has(prev.id) ? null : prev
          );
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove cars"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to remove cars"));
        return result;
      } finally {
        setRemovingIds((prev) => {
          const next = new Set(prev);
          ids.forEach((id) => next.delete(id));
          return next;
        });
      }
    },
    [removeManyFromList]
  );

  // ============================================
  // Convenience Methods
  // ============================================

  // Load more cars (next page, append)
  // Uses refs for filters, sort, and current page to avoid stale closures.
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    const nextPage = currentPageRef.current + 1;
    await list(
      lastQueriedFiltersRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loadingMore, hasMore]);

  // Refresh cars (reset to page 1)
  // Uses refs so it always picks up the last-queried filters/sort.
  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFiltersRef.current,
      { page: 1, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ============================================
  // Lookup Helpers
  // ============================================

  const getCarById = useCallback(
    (id: string | undefined): Car | undefined => {
      if (!id) return undefined;
      return carsById.get(id);
    },
    [carsById]
  );

  const getUserCarAssignment = useCallback(
    (carId: string | undefined, userId: string | undefined): UserCar | undefined => {
      if (!carId || !userId) return undefined;
      const car = carsById.get(carId);
      if (!car?.carUsers) return undefined;
      return car.carUsers.find((cu) => cu.userId === userId);
    },
    [carsById]
  );

  const getUserRoleForCar = useCallback(
    (carId: string | undefined, userId: string | undefined): number | null => {
      const assignment = getUserCarAssignment(carId, userId);
      return assignment?.roleId ?? null;
    },
    [getUserCarAssignment]
  );

  const isCarOwner = useCallback(
    (carId: string | undefined, userId: string | undefined): boolean => {
      const roleId = getUserRoleForCar(carId, userId);
      return roleId === USER_CAR_ROLES.OWNER;
    },
    [getUserRoleForCar]
  );

  const isCarDriver = useCallback(
    (carId: string | undefined, userId: string | undefined): boolean => {
      const roleId = getUserRoleForCar(carId, userId);
      return roleId === USER_CAR_ROLES.DRIVER;
    },
    [getUserRoleForCar]
  );

  const isCarViewer = useCallback(
    (carId: string | undefined, userId: string | undefined): boolean => {
      const roleId = getUserRoleForCar(carId, userId);
      return roleId === USER_CAR_ROLES.VIEWER;
    },
    [getUserRoleForCar]
  );

  const canEditCar = useCallback(
    (carId: string | undefined, userId: string | undefined): boolean => {
      const roleId = getUserRoleForCar(carId, userId);
      return roleId === USER_CAR_ROLES.OWNER || roleId === USER_CAR_ROLES.DRIVER;
    },
    [getUserRoleForCar]
  );

  // Current user convenience helpers (use userId from context)
  const isCurrentUserOwner = useCallback(
    (carId: string | undefined): boolean => isCarOwner(carId, userId ?? undefined),
    [isCarOwner, userId]
  );

  const isCurrentUserDriver = useCallback(
    (carId: string | undefined): boolean => isCarDriver(carId, userId ?? undefined),
    [isCarDriver, userId]
  );

  const canCurrentUserEdit = useCallback(
    (carId: string | undefined): boolean => canEditCar(carId, userId ?? undefined),
    [canEditCar, userId]
  );

  // ============================================
  // Effects
  // ============================================

  // Handle user change - reset and reload data when userId changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ============================================
  // Return
  // ============================================

  return useMemo(
    () => ({
      // Data state
      car,
      cars,
      loading,
      isLoaded,
      loadingMore,
      hasMore,
      isEmpty,

      // Search and filters
      filters,
      sortBy,

      // Pagination
      currentPage,
      pageSize: pageSizeRef.current,

      // Operations state
      isCreating,
      isUpdating,
      isRemoving,
      removingIds,
      selectedCar,

      // Core operations
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,

      // Additional convenience methods
      loadMore,
      refresh,

      // UI state management
      setSelectedCar,

      // Helper operations
      reset,

      // Lookup helpers
      carsById,
      getCarById,
      getUserCarAssignment,
      getUserRoleForCar,
      isCarOwner,
      isCarDriver,
      isCarViewer,
      canEditCar,

      // Current user convenience helpers
      isCurrentUserOwner,
      isCurrentUserDriver,
      canCurrentUserEdit,

      // Error handling
      lastError,
      clearError,
    }),
    [
      car,
      cars,
      loading,
      isLoaded,
      loadingMore,
      hasMore,
      isEmpty,
      filters,
      sortBy,
      currentPage,
      isCreating,
      isUpdating,
      isRemoving,
      removingIds,
      selectedCar,
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,
      loadMore,
      refresh,
      reset,
      carsById,
      getCarById,
      getUserCarAssignment,
      getUserRoleForCar,
      isCarOwner,
      isCarDriver,
      isCarViewer,
      canEditCar,
      isCurrentUserOwner,
      isCurrentUserDriver,
      canCurrentUserEdit,
      lastError,
      clearError,
    ]
  );
};